import { Flex, PseudoBox, useColorMode } from '@chakra-ui/core';
import { useObserver } from 'mobx-react';
import React, { useEffect, useRef, useState } from 'react';
import { ColorExtractor } from 'react-color-extractor';
import { useHistory, useParams } from 'react-router-dom';

import Main from '../components/Main';
import Sidebar from '../components/Sidebar';
import Toast from '../components/Toast';
import { useStores } from '../hooks/useStores';
import spotifyConnectService from '../services/spotifyConnectService';
import { ShareLinkParams } from '../stores/ConfigStore';
import { UserRoles } from '../types/shared';
import { IWebPlaybackState } from '../types/spotify';
import config from '../utils/config';

function useStoreData() {
    const { store } = useStores();
    return useObserver(() => ({
        currentTrack: store.playback.currentTrack,
        setCurrentTrackColors: store.config.setCurrentTrackColors,
        currentTrackColors: store.config.currentTrackColors,
        validatePartyLink: store.config.validatePartyLink,
        isGuest: store.auth.role === UserRoles.GUEST,
        isLinkValid: store.config.isLinkValid,
        closeAllPanels: store.config.closeAllPanels,
        setSearchTerm: store.search.setSearchTerm,
        loadBackupTracks: store.playback.loadBackupTracks,
        playTrack: store.playback.playTrack,
        getNextTrack: store.playback.getNextTrack,
        setProgress: store.playback.setProgress,
        userRole: store.auth.role,
        startListenQueueChanges: store.playback.startListenQueueChanges,
        stopListenQueueChanges: store.playback.stopListenQueueChanges,
        isPlayerReady: store.playback.isPlayerReady,
        queue: store.playback.queueItemsSortedByOrder,
    }));
}

const Home: React.FC = () => {
    const {
        currentTrack,
        setCurrentTrackColors,
        currentTrackColors,
        validatePartyLink,
        isGuest,
        isLinkValid,
        closeAllPanels,
        setSearchTerm,
        loadBackupTracks,
        playTrack,
        getNextTrack,
        setProgress,
        userRole,
        startListenQueueChanges,
        stopListenQueueChanges,
        isPlayerReady,
        queue,
    } = useStoreData();
    const history = useHistory();
    const routeParams = useParams<ShareLinkParams>();
    const playerState = useRef<IWebPlaybackState>();

    const { colorMode } = useColorMode();
    const defaultBgColor = { light: 'gray.50', dark: 'gray.800' };
    const color = { light: 'gray.800', dark: 'gray.50' };
    const [colorsChanged, setColorsChanged] = useState(false);
    const [gradient, setGradient] = useState('');

    const keyPressHandler = (e: KeyboardEvent) => {
        if (e.keyCode === 27) {
            closeAllPanels();
            setSearchTerm('');
        }
    };

    useEffect(() => {
        console.log('userRole', userRole);
        console.log('isPlayerReady', isPlayerReady);
        if (userRole === UserRoles.HOST && isPlayerReady) {
            loadBackupTracks();
            if (queue.length) {
                playTrack(queue[0].track);
                console.log('play - - - - - - - - - -');
            }
            if (spotifyConnectService.player) {
                spotifyConnectService.player.addListener('player_state_changed', (state: IWebPlaybackState | null) => {
                    if (state) {
                        // Getting the current position of the current track
                        if (playerState.current && state.position !== playerState.current.position && !state.paused) {
                            setProgress((state.position / state.duration) * config.PLAYBACK.PROGRESS_BAR_FREQUENCY);
                        }
                        // Detecting when the track ends
                        if (
                            playerState.current &&
                            state.track_window.previous_tracks.find(track => track.id === state.track_window.current_track.id) &&
                            !playerState.current.paused &&
                            state.paused
                        ) {
                            getNextTrack();
                            setProgress(0);
                        }
                        playerState.current = state;
                    }
                });
            }
        }
        startListenQueueChanges();
        return () => {
            if (spotifyConnectService.player) {
                spotifyConnectService.player.removeListener('player_state_changed');
            }
            stopListenQueueChanges();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isPlayerReady]);

    useEffect(() => {
        if (isGuest) {
            validatePartyLink(routeParams).then((isLinkValid: boolean) => {
                if (!isLinkValid) {
                    history.replace('/party-not-found');
                }
            });
        }

        document.addEventListener('keydown', keyPressHandler);
        return () => {
            document.removeEventListener('keydown', keyPressHandler);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Hook to be able to change gradient backgrounds smoothly
    useEffect(() => {
        if (currentTrackColors.length > 2) {
            const angle = 248;
            setGradient(
                `linear-gradient(${angle}deg, ${currentTrackColors[1]} 0%, ${currentTrackColors[0]} 50%, ${currentTrackColors[2]} 100%)`
            );
        }
        setColorsChanged(true);
        setTimeout(() => {
            setColorsChanged(false);
        }, 200);
    }, [currentTrackColors]);

    if (isGuest && !isLinkValid) {
        return <div>Loading...</div>;
    }

    return (
        <PseudoBox
            className="empty-content-before"
            as="div"
            display="flex"
            flexDirection="column"
            minH="100vh"
            maxH="100vh"
            background={gradient}
            bg={defaultBgColor.dark}
            color={color[colorMode]}
            _before={{
                position: 'absolute',
                top: 0,
                bottom: 0,
                left: 0,
                right: 0,
                zIndex: 0,
                background: currentTrackColors[0] || defaultBgColor.dark,
                transition: 'opacity .3s',
                transform: 'rotate3d(0,0,0,0deg)',
                opacity: colorsChanged ? 1 : 0,
                width: '100%',
                height: '100%',
            }}
        >
            {currentTrack && <ColorExtractor maxColors={3} src={currentTrack.track.imageUrl} getColors={setCurrentTrackColors} />}
            <Flex direction={['column-reverse', 'column-reverse', 'row']} position="relative" overflow="hidden" flex={1}>
                <Sidebar />
                <Main />
            </Flex>
            <Toast />
        </PseudoBox>
    );
};

export default Home;
